/**
 * Record identity — the primitives every surface shares for "which field NAMES this record, and how
 * is its value rendered".
 *
 * ## What changed at s199 (FIELD-ROLE-IDENTITY-1)
 *
 * This module used to hold `RECORD_FIELD_BY_SECTION`, a hand-curated map of section slug → identity
 * field key, plus two slug-keyed resolvers over it. **That map is DELETED.** Identity is now declared
 * on the FIELD itself (`identityOrdinal` in `shared/template-spec/`, converged to
 * `generic_fields.identity_ordinal`), so the map had become a second source of truth for something
 * the field already stated. Keeping it as a guard-checked copy was weighed against deriving it and
 * rejected — a guarded copy is still a copy, and drift is then only caught by a test someone has to
 * keep honest (D-199.17).
 *
 * ## Where the answer comes from now, and why the split is where it is
 *
 * 🔴 Two shapes, because two kinds of caller exist and neither should pay for the other:
 *
 *   • **Callers holding real field rows** — the category page, the drawer, the WIE aggregator — use
 *     `identityKeysFromFields` HERE. The declaration arrives in the field payload they already
 *     fetch (`generic_fields` is selected whole), so the browser needs no rule and no constant.
 *   • **Server callers holding only a section slug** — the planner lens, the snapshot, the binding
 *     resolver — use `server/record-identity.ts`, which derives the answer from `TEMPLATE_SPEC`.
 *
 * 🔴 The spec is NOT imported here, deliberately: this module is reachable from client runtime code,
 * and pulling `TEMPLATE_SPEC` in would put **534K / 14,748 lines** into the browser bundle for a
 * label (D-199.18).
 */
import { parseAddress, streetSegment } from "../address";
import { renderCellText } from "../cell-text";
import { parseReference } from "../reference-value";

/** The minimum a field row must carry for its identity role to be read. Both the DB row type
 *  (`GenericField`) and the template-spec field shape satisfy this structurally. */
export interface IdentityDeclaringField {
  fieldKey: string;
  /** s199: 1 = the record's primary identity; 2+ are further parts of a composite name. */
  identityOrdinal?: number | null;
}

/**
 * The ORDERED field keys that together name a record, read off that section's own field rows.
 *
 * Almost every section returns one key. Two sections name a PERSON, and s156 (BENEFICIARY-FILL-1)
 * split their single name field into first/last so gov-forms could fill the parts — so naming those
 * records takes two keys, joined. Titling them by one key alone produced "Barber" on all four of a
 * family's children, and "Complete" on every beneficiary sharing a surname.
 *
 * 🔴 Order is the ORDINAL and nothing else — never `sortOrder` (both composite pairs tie at 0),
 * never object-key order, never sort stability. Before s199 the right answer fell out by accident;
 * the ordinal states it.
 */
export function identityKeysFromFields(fields: readonly IdentityDeclaringField[] | undefined): string[] {
  return (fields ?? [])
    .filter((f) => typeof f.identityOrdinal === "number" && (f.identityOrdinal as number) > 0)
    .slice()
    .sort((a, b) => (a.identityOrdinal as number) - (b.identityOrdinal as number))
    .map((f) => f.fieldKey);
}

/** The PRIMARY identity key — element 0. D-162.9: a composite is DISPLAY-only, so the snapshot
 *  payload and the executor change log keep this key rather than the joined pair. */
export function identityKeyFromFields(fields: readonly IdentityDeclaringField[] | undefined): string | undefined {
  return identityKeysFromFields(fields)[0];
}

/**
 * FIELD-ROLE-IDENTITY-1 ruling 4 (s199) — render ONE identity value for display.
 *
 * 🔴 G7, not preference. Three surfaces needed this and each had grown its own answer:
 *   • `computeRecordLabel` (WIE aggregator) formatted on `fieldType === "address"`;
 *   • `formatPreviewValue` (collapsed-row preview) formatted on whether the VALUE parses;
 *   • the WIE `itemName` path did not format at all, and was protected only by a
 *     `["text","select"]` type filter that quietly stood in for the missing formatting.
 * That third one is why an `address` identity would have printed raw JSON on a planner surface —
 * an F-5 breach — the moment the filter was lifted. The filter was never a guard; it was the
 * absence of this function wearing a guard's clothes.
 *
 * Detection is by VALUE, not by `fieldType`: `parseAddress` only succeeds on a JSON object, so a
 * plain string ("RBC", "Passport") is returned untouched, and an address stored on a field whose
 * type says otherwise is still formatted rather than leaked.
 */
export function renderIdentityValue(raw: string | null | undefined): string {
  const v = (raw ?? "").trim();
  if (!v) return "";
  // s202 (D-202.4): a REFERENCE value (`{ display, ref }` — an institution today, a person
  // under PEOPLE-RECORD-1) renders its display part. 🔴 This branch is FIRST on purpose. A
  // reference must never reach the address join below: that join reads the six address keys,
  // finds none, and returns "" — so an identity field would go BLANK rather than wrong, which
  // is the harder failure to see. `parseAddress` is now strict (s202) and would reject a
  // reference anyway; the ordering here does not depend on that, and neither function depends
  // on the other, so both remain correct if either is changed alone.
  // s206 (CELL-RENDER-FORMATTER-1): the shape rules are the SHARED `renderCellText`; what stays
  // here is the one thing genuinely specific to an identity — the address NARROWING.
  //
  // 🔴 Why this is not simply `streetSegment(renderCellText(v))`: `streetSegment` cuts at the first
  // comma, which is right for an address and WRONG for a reference — a display name of
  // "Smith, John" would render as "Smith". The narrowing has to apply to the address branch alone,
  // so the branches stay separate here even though both now delegate their formatting.
  if (parseReference(v)) return renderCellText(v);
  // F-5: never render raw `address` JSON — collapse to the street segment.
  if (parseAddress(v)) return streetSegment(renderCellText(v));
  return v;
}

/** Join a record's identity parts into its display name. Blank parts are skipped. */
export function composeRecordLabel(parts: Array<string | null | undefined>): string {
  return parts.map((p) => (p ?? "").trim()).filter(Boolean).join(" ");
}
